import { useEffect, useRef, useState, type ComponentProps, type ReactNode } from "react";
import type { TFunction } from "i18next";
import { Car, Shield, Sparkles, ChevronDown, ChevronUp, Ban, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@workspace/ui/components/ui/dialog";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import { RollingNumber, useReducedMotion } from "@/components/ui/rolling-number";
import { LiveStatus } from "@/components/ui/live-status";
import { cn } from "@/lib/utils";
import { useFormatMoney } from "@/lib/formatMoney";
import { CheckoutDatesEditor } from "@/components/checkout/CheckoutDatesEditor";
import { CheckoutRentalTypeEditor } from "@/components/checkout/CheckoutRentalTypeEditor";
import { CheckoutDeliverySection } from "@/components/checkout/CheckoutDeliverySection";
import type { RentalType } from "@/components/checkout/CheckoutRentalTypeEditor";
import type { PriceQuote } from "@/lib/bookings-api";
import type { AvailabilityData } from "@/lib/api";
import { formatLocationLabel } from "@/lib/trip-location";
import { depositCopyKey, type DepositCopyVariant } from "@/lib/checkout-deposit-copy";

type CancellationPolicy = "non-refundable" | "flexible";

// Expand the interactive area to 44px without making the compact rail taller.
const summaryActionClass = "relative rounded-sm text-xs font-medium text-foreground underline after:absolute after:left-1/2 after:top-1/2 after:h-full after:w-full after:min-h-11 after:min-w-11 after:-translate-x-1/2 after:-translate-y-1/2 after:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

type CheckoutCarInfo = {
  id?: number; make: string; model: string; year: number; city: string; state: string; slug?: string;
  countryCode: string | null;
  imageUrl: string | null; ownerName: string | null; ownerImage: string | null;
  ownerSinceYear?: number | null;
  rating?: number; reviewCount?: number; guestFavourite?: boolean; popular?: boolean;
  latitude?: number | null; longitude?: number | null;
  rentalDrivingActive?: boolean;
  rentalChauffeuredActive?: boolean;
  rentalEventActive?: boolean;
  selfDriveDeliveryEnabled?: boolean;
  deliveryEnabled?: boolean;
  deliveryMaxRadius?: number | null;
  deliveryAirports?: Array<{ airport_code: string; airport_name: string; flat_fee: number }>;
} | null;

export function CheckoutSummary({
  t,
  depositCopyVariant,
  carInfo,
  quote,
  currency,
  selectedCancellationPolicy,
  dateTimeLabel,
  dateRangeLabel,
  rentalTypeLabel,
  rentalType,
  dateFrom,
  dateTo,
  pickupTimeParam,
  dropoffTimeParam,
  hoursParam,
  availability,
  loadingAvailability,
  onApplyDates,
  rentalTypeOptions,
  onSwitchRentalType,
  airportCodeParam,
  deliveryEngaged,
  deliveryAddress,
  deliveryDistanceParam,
  deliveryLatitude,
  deliveryLongitude,
  onApplyLocation,
  updatingQuote,
  taxRecalcInFlight,
  quoteStalled = false,
  onRetryQuote,
  renderPriceRows,
  renderDual,
  totalDue,
  fullPaymentDue = totalDue,
  climateOffsetExtra = 0,
  mobileSheetOpen,
  onToggleMobileSheet,
  booking,
  deliveryRangeError,
  rentalEligibilityError,
  phoneGateBlocking,
  payButtonLabel,
  onBlockedPayTap,
  onIdentityContinue,
  reserveFormError,
  paymentSafetyDisabled = false,
}: {
  t: TFunction;
  depositCopyVariant?: DepositCopyVariant | null;
  carInfo: CheckoutCarInfo;
  quote: PriceQuote;
  currency: string;
  selectedCancellationPolicy: CancellationPolicy;
  dateTimeLabel: string;
  dateRangeLabel: string;
  rentalTypeLabel: string;
  rentalType: RentalType;
  dateFrom: string;
  dateTo: string;
  pickupTimeParam: string | undefined;
  dropoffTimeParam: string | undefined;
  hoursParam: number | undefined;
  availability: AvailabilityData | null;
  loadingAvailability: boolean;
  onApplyDates: ComponentProps<typeof CheckoutDatesEditor>["onApply"];
  rentalTypeOptions: ComponentProps<typeof CheckoutRentalTypeEditor>["options"];
  onSwitchRentalType: ComponentProps<typeof CheckoutRentalTypeEditor>["onSelect"];
  airportCodeParam: string | undefined;
  deliveryEngaged: boolean;
  deliveryAddress: string | null;
  deliveryDistanceParam: number | undefined;
  deliveryLatitude: number | null;
  deliveryLongitude: number | null;
  onApplyLocation: ComponentProps<typeof CheckoutDeliverySection>["onApplyLocation"];
  updatingQuote: boolean;
  taxRecalcInFlight: boolean;
  /**
   * Task #8483 — true when the most recent price refresh hit the client's
   * own stall watchdog (see `QuoteTimeoutError` in bookings-api.ts) rather
   * than settling normally. Distinct from `updatingQuote`/`taxRecalcInFlight`
   * (which mean "still working") — this means "gave up waiting, needs a
   * retry". The last-known-good price stays on screen; only the retry
   * affordance and Pay-button gating change.
   */
  quoteStalled?: boolean;
  /** Re-fires the price fetch. Required when `quoteStalled` can be true. */
  onRetryQuote?: () => void;
  renderPriceRows: (opts?: { includeHidden?: boolean }) => ReactNode;
  renderDual: (chargeAmount: number) => { preferred: string; charge: string } | null;
  totalDue: number;
  /** Task #3465: full booking commitment (card + on-pickup). Equals totalDue for full-pay markets. Used for the headline "Total" display and price breakdown anchor. */
  fullPaymentDue?: number;
  /** Task #3523: opted-in climate offset (local currency, 0 when off). Added to the partial-pay per-card charge lines so they match the headline Total and the Stripe PaymentIntent amount. */
  climateOffsetExtra?: number;
  mobileSheetOpen: boolean;
  onToggleMobileSheet: () => void;
  booking: boolean;
  deliveryRangeError: string | null;
  rentalEligibilityError?: string | null;
  phoneGateBlocking: boolean;
  payButtonLabel: string;
  /** Task #6144 — present only when the pay button is blocked by a
   *  USER-ACTIONABLE gate. Tapping the disabled sticky-bar CTA then scrolls
   *  to + highlights the blocking section and captures the
   *  checkout_pay_blocked_tap event (handled by the page). Transient
   *  disables (quote updating, booking in flight) pass undefined and the
   *  tap stays inert. */
  onBlockedPayTap?: () => void;
  onIdentityContinue?: () => void;
  reserveFormError?: string | null;
  paymentSafetyDisabled?: boolean;
}) {
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const summaryRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const node = summaryRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const measure = () => node.style.setProperty("--checkout-summary-height", `${node.getBoundingClientRect().height}px`);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const reducedMotion = useReducedMotion();
  // Task #3945 — while the headline total churns, soften the line-item
  // breakdown above it (blur + fade) so the eye lands on the rolling
  // figure. Skipped for reduced-motion users.
  const recalcInFlight = updatingQuote || taxRecalcInFlight;
  const breakdownBlurClass = cn(
    "transition-[filter,opacity] duration-300",
    recalcInFlight && !reducedMotion && "blur-[2px] opacity-50",
  );
  const { money: formatMoneyWithCurrency } = useFormatMoney();
  return (
    <>
      {/* RIGHT COLUMN — sticky summary ------------------------------------ */}
      <aside ref={summaryRef} data-testid="checkout-summary-rail"
        className="hidden min-w-0 md:sticky md:col-span-2 md:block md:self-start"
        style={{ top: "min(calc(var(--header-h) + 4.5rem), calc(100svh - var(--checkout-summary-height, 0px) - 1rem))" }}>
        <div className="checkout-rail space-y-3">
          <div className="rounded-xl border border-border p-4 shadow-sm">
            {/* Keep the car prominent while bounding the hero on short screens. */}
            <div className="min-w-0">
              {carInfo?.imageUrl ? (
                <img
                  src={carInfo.imageUrl}
                  alt={`${carInfo?.year} ${carInfo?.make} ${carInfo?.model}`}
                  className="aspect-video max-h-[24svh] w-full rounded-lg object-cover"
                />
              ) : (
                <div className="flex aspect-video max-h-[24svh] w-full items-center justify-center rounded-lg bg-muted">
                  <Car className="h-10 w-10 text-muted-foreground" />
                </div>
              )}
              <div className="mt-3 flex min-w-0 items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2 text-sm font-semibold leading-snug text-foreground">{carInfo ? `${carInfo.year} ${carInfo.make} ${carInfo.model}` : ""}</p>
                <p className="truncate text-xs text-muted-foreground">{carInfo ? formatLocationLabel(carInfo.city, carInfo.state) : ""}</p>
                {(carInfo?.rating != null || carInfo?.guestFavourite) && (
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-foreground">
                    {carInfo?.rating != null && <span>★ {Number(carInfo.rating).toFixed(2)}{carInfo.reviewCount ? <span className="text-muted-foreground"> · {t("checkout.reviews_count", { count: carInfo.reviewCount })}</span> : null}</span>}
                    {carInfo?.guestFavourite && <span className="inline-flex items-center gap-1 font-medium"><Sparkles className="h-3 w-3" aria-hidden="true" /> {t("checkout.guest_favourite")}</span>}
                  </p>
                )}
              </div>
              {carInfo?.make && (
                <img
                  src={`${import.meta.env.BASE_URL || "/"}images/car-logos/128/${carInfo.make.toLowerCase().replace(/\s+/g, "-")}.webp`}
                  alt={t("checkout.car_logo_alt", { make: carInfo.make })}
                  title={carInfo.make}
                  className="h-7 w-auto max-w-12 shrink-0 object-contain opacity-90"
                  style={{ filter: "grayscale(1) contrast(1.05)" }}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              )}
              </div>
            </div>

            {/* Cancellation policy summary */}
            <div className="mt-3 border-t border-border pt-3">
              <p className="text-sm font-semibold text-foreground">
                {selectedCancellationPolicy === "flexible" ? t("checkout.free_cancellation") : t("checkout.non_refundable_short")}
              </p>
              <p className="mt-0.5 text-xs leading-snug text-muted-foreground">
                {quote.paymentType === "partial"
                  ? t("checkout.non_refundable_partial_desc", { defaultValue: "No refund if you cancel. Platform fees are non-refundable on partial-payment bookings." })
                  : (quote.cancellationPolicyDetails?.description ||
                      (selectedCancellationPolicy === "flexible"
                        ? t("checkout.flexible_default_desc")
                        : t("checkout.non_refundable_default_desc")))}
              </p>
              <button type="button" onClick={() => setPolicyModalOpen(true)} className={cn("mt-1", summaryActionClass)}>{t("checkout.full_policy")}</button>
            </div>

            {/* Dates — inline edit (#824). Opens an Airbnb-style popover so
                the renter can swap dates / times / hours without losing any
                in-progress checkout state (message, promo, payment plan…). */}
            <div className="mt-3 flex items-start justify-between gap-3 border-t border-border pt-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">{t("checkout.dates_label")}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{dateTimeLabel}</p>
              </div>
              <CheckoutDatesEditor
                trigger={
                  <button type="button" className={summaryActionClass}>
                    {t("common.change")}
                  </button>
                }
                rentalType={rentalType}
                dateFrom={dateFrom}
                dateTo={dateTo}
                pickupTime={pickupTimeParam || "10:00 am"}
                dropoffTime={
                  dropoffTimeParam ||
                  // Mirror BookingWidget's getDefaultTimes(): self-drive
                  // defaults to 10pm drop-off; hourly to 12pm so the
                  // editor opens with a valid 2-hour span. Bare "10:00"
                  // would parse as the timeToHours fallback (8h) AND
                  // collide with pickup, blocking Apply on legacy deep
                  // links that didn't carry the time params.
                  (rentalType === "driving" ? "10:00 pm" : "12:00 pm")
                }
                hours={hoursParam}
                availability={availability}
                loadingAvailability={loadingAvailability}
                onApply={onApplyDates}
              />
            </div>

            {/* Rental type — inline switcher (#824). Suppressed automatically
                when the listing only supports a single rental type. */}
            <div className="mt-3 flex items-start justify-between gap-3 border-t border-border pt-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">{t("checkout.rental_type_label")}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{rentalTypeLabel}</p>
              </div>
              <CheckoutRentalTypeEditor
                trigger={
                  <button type="button" className={summaryActionClass}>
                    {t("common.change")}
                  </button>
                }
                current={rentalType}
                options={rentalTypeOptions}
                onSelect={onSwitchRentalType}
              />
            </div>

            {/* Pick-up & drop-off — inline location editor (#824 follow-up).
                Title row mirrors the Dates/Rental-type editors with a
                "Change" link that opens a popover where the renter can
                switch between free pickup at the car's location, address
                delivery, or airport delivery. The static map below always
                shows the car's location since that's the origin point for
                any delivery distance/fee calculation. */}
            {/* The location row always renders (#840) — legacy listings
                without lat/long still need a Pick-up & drop-off line so
                renters can see where they're picking up and (if delivery
                is enabled) switch to delivery from checkout. The map and
                the "approximate location" note are the only parts gated
                on lat/long. The Change affordance only renders when the
                host has delivery enabled, mirroring the quote widget. */}
            <div className="checkout-rail-location">
            <CheckoutDeliverySection
                actionClassName={summaryActionClass}
              collapsibleMap
              t={t}
              carInfo={carInfo}
              quote={quote}
              rentalType={rentalType}
              airportCodeParam={airportCodeParam}
              deliveryEngaged={deliveryEngaged}
              deliveryAddress={deliveryAddress}
              deliveryDistanceParam={deliveryDistanceParam}
              deliveryLatitude={deliveryLatitude}
              deliveryLongitude={deliveryLongitude}
              onApplyLocation={onApplyLocation}
            />
            </div>

            {/* Price details */}
            <div className="mt-3 border-t border-border pt-3">
              <p className="text-sm font-semibold text-foreground">{t("checkout.price_details")}</p>
              {/* a11y — announce price recalculation status to screen readers
                  without changing the visual layout. */}
              <LiveStatus srOnly as="div">
                {recalcInFlight
                  ? t("checkout.updating_price")
                  : t("checkout.a11y.total_updated", {
                      defaultValue: "Total updated: {{total}}",
                      total: formatMoneyWithCurrency(fullPaymentDue, currency),
                    })}
              </LiveStatus>
              {updatingQuote ? (
                <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground"><Spinner size="sm" /> {t("checkout.updating_price")}</div>
              ) : quoteStalled ? (
                // Task #8483 — replaces the spinner once it's clear the
                // request stalled rather than merely being slow. Showing
                // the last-known price above (unblurred, not gated on this
                // flag) while offering an explicit retry here matches the
                // incident's "no infinite spinner, always a next step" fix.
                <div
                  role="alert"
                  className="mt-2 flex items-center justify-between gap-2 rounded-md border border-amber-200/60 bg-amber-50/60 px-2.5 py-1.5 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-200"
                >
                  <span>{t("checkout.quote_stalled_inline", "Couldn't refresh your price")}</span>
                  {onRetryQuote && (
                    <button
                      type="button"
                      onClick={onRetryQuote}
                      data-testid="checkout-quote-retry"
                      className="shrink-0 font-semibold underline underline-offset-2"
                    >
                      {t("checkout.try_again", "Try again")}
                    </button>
                  )}
                </div>
              ) : null}
              <div className={cn("mt-3 space-y-2 text-sm", breakdownBlurClass)}>
                {renderPriceRows({ includeHidden: false })}
              </div>
              <div className="mt-3 flex items-start justify-between border-t border-border pt-3 text-base font-semibold gap-3">
                <span>{t("checkout.total_currency", { currency })}</span>
                {(() => {
                  // Task #2342 dual-currency total. When charge ==
                  // preferred we just show the single charge string.
                  const dual = renderDual(fullPaymentDue);
                  if (!dual) return (
                    <RollingNumber
                      data-testid="checkout-total-amount"
                      value={fullPaymentDue}
                      loading={updatingQuote || taxRecalcInFlight}
                      format={(n) => formatMoneyWithCurrency(n, currency)}
                    />
                  );
                  return (
                    <span className="flex flex-col items-end" data-testid="checkout-total-dual">
                      <span>{dual.preferred}</span>
                      <span className="text-xs font-normal text-muted-foreground">
                        {t("checkout.charged_as", { defaultValue: "charged as" })} {dual.charge}
                      </span>
                    </span>
                  );
                })()}
              </div>

              {/* Task #2342 — partial-payment three-line breakdown.
                  Only the platform-charged "pay today" portion gets
                  dual display; the host-collected on-pickup and
                  owner-receives lines are paid host-direct in their
                  local currency, so single-line is correct. */}
              {quote.paymentType === "partial" && (
                <div
                  className="mt-3 rounded-lg border border-border bg-muted/30 p-3 text-xs"
                  data-testid="checkout-partial-payment-breakdown"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-muted-foreground">
                      {t("checkout.partial.pay_now", { defaultValue: "Pay now (charged to your card)" })}
                    </span>
                    {(() => {
                      // Task #3523: the per-card "Pay now" charge must include the
                      // opted-in climate offset, the same way the headline Total
                      // (`totalDue`/`fullPaymentDue`) and the Stripe PaymentIntent
                      // (`request.ts` adds `climateAddon`) already do. Binding to the
                      // raw `quote.platformPaymentDue` understated the card charge.
                      const payNow = Math.round((quote.platformPaymentDue + climateOffsetExtra) * 100) / 100;
                      const dual = renderDual(payNow);
                      if (!dual) return <span className="font-semibold text-foreground">{formatMoneyWithCurrency(payNow, currency)}</span>;
                      return (
                        <span className="flex flex-col items-end font-semibold text-foreground">
                          <span>{dual.charge}</span>
                          <span className="text-[10px] font-normal text-muted-foreground">≈ {dual.preferred}</span>
                        </span>
                      );
                    })()}
                  </div>
                  <div className="mt-2 flex items-start justify-between gap-2 border-t border-border pt-2">
                    <span className="text-muted-foreground">
                      {t("checkout.partial.owner_receives", { defaultValue: "Owner receives directly" })}
                    </span>
                    <span className="font-semibold text-foreground">{formatMoneyWithCurrency(quote.ownerEarn, currency)}</span>
                  </div>
                  {quote.chargeCurrency
                    && quote.chargeCurrency !== currency
                    && Number(quote.fxRate) > 0
                    && Number(quote.fxRate) !== 1 && (
                    <p className="mt-2 border-t border-border pt-2 text-[11px] text-muted-foreground" data-testid="checkout-fx-disclosure">
                      {(() => {
                        // Task #3523: the FX disclosure must include the opted-in
                        // climate offset, FX-converted at the same snapshotted rate
                        // as the rest of the charge — mirroring the backend, which
                        // adds `climateAddon = convertToCapture(climateOffsetAmount)`
                        // to the PaymentIntent (`request.ts`). The base
                        // `quote.chargeAmount` excludes the offset, so add it here.
                        const fxRate = Number(quote.fxRate);
                        const baseCharge = quote.chargeAmount ?? quote.platformPaymentDue * fxRate;
                        const offsetCharge = Math.round(climateOffsetExtra * fxRate * 100) / 100;
                        const chargeWithOffset = Math.round((baseCharge + offsetCharge) * 100) / 100;
                        return t("checkout.partial.fx_disclosure", {
                          defaultValue: "You'll be charged {{chargeAmount}}.",
                          chargeAmount: formatMoneyWithCurrency(chargeWithOffset, quote.chargeCurrency),
                        });
                      })()}
                    </p>
                  )}
                </div>
              )}

            </div>
            {quote.deposit > 0 && quote.paymentType !== "partial" && (
              <Alert variant="warning" icon={Shield} className="mt-2">
                <div className="flex justify-between">
                  <AlertTitle>{t("checkout.security_deposit")}</AlertTitle>
                  <span className="font-medium text-sm">{formatMoneyWithCurrency(quote.deposit, currency)}</span>
                </div>
                <AlertDescription className="text-[11px]">
                  {t(depositCopyKey(depositCopyVariant ?? "generic", "note"))}
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Rare find / social proof banner */}
          {(carInfo?.guestFavourite || carInfo?.popular) && (
            <div className="rounded-xl border border-border p-4">
              <p className="text-sm font-semibold text-foreground">{t("checkout.rare_find")}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("checkout.rare_find_desc")}
              </p>
            </div>
          )}
        </div>
      </aside>

      {/* MOBILE bottom bar (tap totals to expand the breakdown) */}
      <div className="checkout-mobile-bar fixed inset-x-0 bottom-0 z-40 border-t border-border material-panel pb-safe md:hidden">
        {reserveFormError && !paymentSafetyDisabled && (
          <p role="alert" data-testid="checkout-reserve-validation-sticky" className="px-4 pt-2 text-xs font-medium text-destructive">
            {reserveFormError}
          </p>
        )}
        {mobileSheetOpen && (
          <div id="checkout-mobile-price-sheet" role="region" aria-label={t("checkout.price_details")} className="checkout-mobile-sheet max-h-[55vh] overflow-y-auto overscroll-contain border-b border-border px-4 py-4">
            <div className="checkout-mobile-trip mb-3 flex items-center gap-3">
              {carInfo?.imageUrl ? (
                <img src={carInfo.imageUrl} alt="" className="h-12 w-16 shrink-0 rounded-md object-cover" />
              ) : (
                <div className="flex h-12 w-16 items-center justify-center rounded-md bg-muted"><Car className="h-5 w-5 text-muted-foreground" /></div>
              )}
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{carInfo ? `${carInfo.year} ${carInfo.make} ${carInfo.model}` : ""}</p>
                <p className="checkout-mobile-trip-meta truncate text-xs text-muted-foreground">{dateRangeLabel} · {rentalTypeLabel}</p>
              </div>
            </div>
            <div className={cn("checkout-mobile-rows space-y-2 border-t border-border pt-3 text-sm", breakdownBlurClass)}>
              {renderPriceRows({ includeHidden: false })}
            </div>
            <div className="checkout-mobile-total mt-3 flex items-center justify-between border-t border-border pt-3 text-base font-semibold">
              <span>{t("checkout.total_currency", { currency })}</span>
              <RollingNumber
                value={fullPaymentDue}
                loading={updatingQuote || taxRecalcInFlight}
                format={(n) => formatMoneyWithCurrency(n, currency)}
              />
            </div>
            {/* Task #853: refundable deposits only ever apply to self-drive bookings.
                The backend quote returns 0 for chauffeur/event, but we also gate on
                rentalType so the UI never surfaces a hold for those rental types. */}
            {rentalType === "driving" && quote.deposit > 0 && (
              <div className="mt-3 flex items-center justify-between rounded-lg border border-amber-200/60 bg-amber-50/60 p-3 text-xs dark:border-amber-900/40 dark:bg-amber-950/20">
                <span className="flex items-center gap-2 text-foreground"><Shield className="h-4 w-4 text-amber-600" /> {t("checkout.security_deposit")}</span>
                <span className="font-semibold text-foreground">{formatMoneyWithCurrency(quote.deposit, currency)}</span>
              </div>
            )}
          </div>
        )}
        {quoteStalled && !updatingQuote && (
          <div role="alert" data-testid="checkout-quote-stalled-sticky" className="checkout-mobile-stalled mx-4 mt-2 flex items-center justify-between gap-2 rounded-lg border border-amber-200/60 bg-amber-50/70 px-3 py-1.5 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-200">
            <span>{t("checkout.quote_stalled_inline", "Couldn't refresh your price")}</span>
            {onRetryQuote && (
              <button type="button" onClick={onRetryQuote} className="touch-44 shrink-0 font-semibold underline underline-offset-2">
                {t("checkout.try_again", "Try again")}
              </button>
            )}
          </div>
        )}
        <div className="checkout-mobile-actions mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-2">
          <button
            type="button"
            onClick={onToggleMobileSheet}
            className="touch-44 flex items-center gap-2 text-left"
            aria-expanded={mobileSheetOpen}
            aria-controls="checkout-mobile-price-sheet"
            aria-label={mobileSheetOpen ? t("checkout.hide_price_breakdown_aria") : t("checkout.show_price_breakdown_aria")}
          >
            <div>
              <p className="text-base font-semibold text-foreground">
                {/* Task #3945 — never render the odometer scroller inside a
                    button. This collapsed bar is a tap target, so show a plain
                    total with a traditional inline spinner while the quote
                    refreshes. The expanded sheet + desktop rail keep the
                    rolling animation (they live in non-interactive rows). */}
                <span className="inline-flex items-center gap-1.5 align-middle">
                  {formatMoneyWithCurrency(fullPaymentDue, currency)}
                  {(updatingQuote || taxRecalcInFlight) && <Spinner size="sm" />}
                </span>{" "}
                <span className="text-xs font-normal text-muted-foreground underline">{t("checkout.total_label")}</span>
              </p>
              {quote.paymentType === "partial" ? (
                <p className="checkout-mobile-paynow text-[11px] text-muted-foreground" data-testid="checkout-mobile-pay-now">
                  {t("checkout.partial.pay_now", { defaultValue: "Pay now (charged to your card)" })}{": "}
                  <span className="font-semibold text-foreground">
                    {formatMoneyWithCurrency(Math.round((quote.platformPaymentDue + climateOffsetExtra) * 100) / 100, currency)}
                  </span>
                </p>
              ) : (
                <p className="text-[11px] text-muted-foreground">{dateRangeLabel}</p>
              )}
            </div>
            {mobileSheetOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronUp className="h-4 w-4 text-muted-foreground" />}
          </button>
          {/* Mobile sticky-bar CTA. The real confirmation happens inline
              (deferred-intent Stripe Elements is mounted within the page, no
              overlay sheet), so a single tap here drives the real inline Pay
              button directly — clicking it runs the exact same confirm
              pipeline AND inherits its full disabled gating (a disabled
              button ignores the programmatic click). When that button is
              absent (earlier step) or disabled, we fall back to scrolling it
              into view so its now-visible helper text explains why. */}
          {/* Task #6144 — a disabled button swallows clicks, so the blocked-tap
              interception lives on a wrapper span; `disabled:pointer-events-none`
              lets the tap pass through to it. Only fires when the disable is a
              user-actionable gate (onBlockedPayTap present). */}
          <span
            onClick={() => {
              const stickyDisabled =
                paymentSafetyDisabled || booking || !!deliveryRangeError || !!rentalEligibilityError || phoneGateBlocking || updatingQuote || taxRecalcInFlight || quoteStalled;
              if (!onIdentityContinue && stickyDisabled && !booking) onBlockedPayTap?.();
            }}
            data-testid="checkout-pay-blocked-catch-sticky"
            className="inline-flex"
          >
            <button
              onClick={() => {
                if (onIdentityContinue) { onIdentityContinue(); return; }
                const realPay = document.querySelector<HTMLButtonElement>(
                  '[data-testid="checkout-pay-button-mobile-inline"]',
                );
                if (realPay && !realPay.disabled) {
                  realPay.click();
                  return;
                }
                const anchor = document.getElementById("checkout-pay-button-anchor");
                // jsdom doesn't implement scrollIntoView; guard so tests
                // (carbonOffset, backNavigation) don't throw on click.
                if (anchor && typeof anchor.scrollIntoView === "function") {
                  anchor.scrollIntoView({ behavior: "smooth", block: "center" });
                }
              }}
              disabled={onIdentityContinue ? booking : paymentSafetyDisabled || booking || !!deliveryRangeError || !!rentalEligibilityError || phoneGateBlocking || updatingQuote || taxRecalcInFlight || quoteStalled}
              data-testid="checkout-pay-button-mobile"
              className="primary-action touch-44 inline-flex items-center justify-center rounded-lg px-5 text-sm font-semibold disabled:opacity-50 disabled:pointer-events-none"
            >
              {booking || recalcInFlight ? <Spinner size="sm" /> : payButtonLabel}
            </button>
          </span>
        </div>
      </div>

      {/* Cancellation policy detail modal */}
      <Dialog open={policyModalOpen} onOpenChange={setPolicyModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {quote.paymentType === "partial" || selectedCancellationPolicy === "non-refundable" ? (
                <Ban className="h-4 w-4 text-destructive" />
              ) : (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              )}
              {quote.paymentType === "partial"
                ? t("checkout.non_refundable_short", { defaultValue: "Non-refundable" })
                : (quote.cancellationPolicyDetails?.label || t("checkout.cancellation_title", { defaultValue: "Cancellation policy" }))}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 space-y-4 text-sm">
            {quote.paymentType === "partial" ? (
              <p className="leading-relaxed text-muted-foreground">
                {t("checkout.non_refundable_partial_desc", { defaultValue: "No refund if you cancel. Platform fees are non-refundable on partial-payment bookings." })}
              </p>
            ) : quote.cancellationPolicyDetails?.rules?.length > 0 ? (
              <ul className="space-y-3">
                {quote.cancellationPolicyDetails.rules.map((rule, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-foreground/40" />
                    <span className="leading-relaxed text-foreground/80">{rule}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="leading-relaxed text-muted-foreground">
                {quote.cancellationPolicyDetails?.description ||
                  (selectedCancellationPolicy === "flexible"
                    ? t("checkout.flexible_default_desc")
                    : t("checkout.non_refundable_default_desc"))}
              </p>
            )}
            {(quote.paymentType !== "partial" && selectedCancellationPolicy === "non-refundable") && (
              <div className="rounded-lg bg-destructive/5 border border-destructive/10 p-3 text-xs text-destructive/80">
                {t("trip.non_refundable_cancel_note", { defaultValue: "This policy is non-refundable. No amount will be returned regardless of when you cancel." })}
              </div>
            )}
            {quote.paymentType === "partial" && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                {t("checkout.partial_cancel_modal_note", { defaultValue: "Because this is a partial-payment booking, the platform fee is non-refundable if you cancel. Any portion paid directly to the host must be arranged separately with them." })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
